import { BaseWebComponentRepository } from '../../../../../framework/BaseWebComponentRepository';
import { IntegrityMonitorCustomRuleBaseDataProvider } from '../dataProvider/IntegrityMonitorCustomRuleBaseDataProvider';
import { IntegrityMonitorCustomRuleFactory } from '../factory/IntegrityMonitorCustomRuleFactory';
import { IntegrityMonitorEntityType, IntegrityMonitorPlatform } from '../../../../common/utils/types';
import {
  CustomRuleActionMapping,
  CustomRuleAttributes,
  CustomRuleFormData,
  CustomRuleRecord,
  CustomRuleSecurityFormData,
} from '../types';

export class IntegrityMonitorCustomRuleRepository extends BaseWebComponentRepository {
  public dataProviderFactory: IntegrityMonitorCustomRuleFactory;
  public dataProvider: IntegrityMonitorCustomRuleBaseDataProvider;

  constructor() {
    super();
    this.initialSetup({
      dataProviderFactory: new IntegrityMonitorCustomRuleFactory(),
    });
  }

  private static convertRulePlatform(rule: CustomRuleFormData): number {
    const entityType = rule.general.entityType;

    if (
      entityType === IntegrityMonitorEntityType.REGISTRYKEY ||
      entityType === IntegrityMonitorEntityType.REGISTRYVALUE
    )
      return IntegrityMonitorPlatform.WINDOWS;

    return rule.security['os'];
  }

  private static convertRuleAttributes(rule: CustomRuleFormData): string[] {
    const attributes = rule.security.attributes;
    return Object.keys(attributes).filter(x => attributes[x].enabled);
  }

  private static convertRuleActions(rule: CustomRuleFormData): CustomRuleActionMapping[] {
    const actionMapping: Object = rule.security.attributes;

    const actionsList: CustomRuleActionMapping[] = [];
    for (const key of Object.keys(actionMapping)) {
      if (!actionMapping[key].enabled || !actionMapping[key].action || actionMapping[key].action === 'no_action')
        continue;

      actionsList.push({
        attribute: key,
        action: actionMapping[key].action,
      });
    }

    return actionsList;
  }

  private static convertRuleSpecifics(rule: CustomRuleFormData): any {
    const entityType = rule.general.entityType;

    switch (entityType) {
      case IntegrityMonitorEntityType.FILE:
        return {
          keys: rule['security']['keys'],
        };

      case IntegrityMonitorEntityType.DIRECTORY:
        return {
          keys: rule['security']['keys'],
        };

      case IntegrityMonitorEntityType.REGISTRYKEY:
        return {
          registryKeys: rule['security']['registryKeys'],
        };

      case IntegrityMonitorEntityType.REGISTRYVALUE:
        return {
          registryValues: rule['security']['registryValues'],
        };
    }
  }

  private static toNull(s: string | null): string | null {
    if (!s) return null;
    return s.length > 0 ? s : null;
  }

  private static convertRule(rule: CustomRuleFormData): CustomRuleRecord {
    return {
      companyId: rule.companyId,
      ruleName: rule.general.name,
      description: this.toNull(rule.general.description),
      severity: rule.general.severity,
      platform: IntegrityMonitorCustomRuleRepository.convertRulePlatform(rule),
      entities: [
        {
          entityType: rule.general.entityType,
          attributes: IntegrityMonitorCustomRuleRepository.convertRuleAttributes(rule),
          actions: IntegrityMonitorCustomRuleRepository.convertRuleActions(rule),
          specifics: IntegrityMonitorCustomRuleRepository.convertRuleSpecifics(rule),
        },
      ],
    };
  }

  private static convertRecordAttributes(record: CustomRuleRecord): CustomRuleAttributes {
    const attributes = record.entities[0].attributes;
    const actions = record.entities[0].actions;

    const res: CustomRuleAttributes = {};

    for (const attribute of attributes) {
      const action = actions !== null && actions.find(item => item.attribute === attribute);
      res[attribute] = { enabled: true, action: action ? action.action : 'no_action' };
    }

    return res;
  }

  private static convertRecordEntity(record: CustomRuleRecord): CustomRuleSecurityFormData {
    const entityType = record.entities[0].entityType;

    const common = {
      attributes: IntegrityMonitorCustomRuleRepository.convertRecordAttributes(record),
    };

    switch (entityType) {
      case IntegrityMonitorEntityType.FILE:
        return {
          os: record.platform,
          keys: record.entities[0].specifics.keys,
          ...common,
        };

      case IntegrityMonitorEntityType.DIRECTORY:
        return {
          os: record.platform,
          keys: record.entities[0].specifics.keys,
          ...common,
        };

      case IntegrityMonitorEntityType.REGISTRYKEY:
        return {
          registryKeys: record.entities[0].specifics.registryKeys,
          ...common,
        };

      case IntegrityMonitorEntityType.REGISTRYVALUE:
        return {
          registryValues: record.entities[0].specifics.registryValues,
          ...common,
        };
    }

    throw new Error(`Bad entity type: ${entityType}`);
  }

  public convertRecord(record: CustomRuleRecord): CustomRuleFormData {
    return {
      _id: record._id,
      companyId: record.companyId,
      general: {
        name: record.ruleName,
        description: record.description,
        severity: record.severity,
        entityType: record.entities[0].entityType,
      },
      security: IntegrityMonitorCustomRuleRepository.convertRecordEntity(record),
    };
  }

  public updateRule(rule: CustomRuleFormData): Promise<Object | null> {
    const record = IntegrityMonitorCustomRuleRepository.convertRule(rule);
    record['_id'] = rule['_id'];
    return this.dataProvider.updateCustomRule(record);
  }

  public async createRule(rule: CustomRuleFormData): Promise<Object | null> {
    const record = IntegrityMonitorCustomRuleRepository.convertRule(rule);
    return this.dataProvider.createCustomRule(record);
  }

  async assignRulesToRuleSets(
    companyId: string,
    ruleIds: Array<string>,
    ruleSetIds: Array<string>
  ): Promise<Object | null> {
    return await this.dataProvider.assignRulesToRuleSets(companyId, ruleIds, ruleSetIds);
  }
}
